import React, { useRef, useState } from 'react';
import styles from './Dropdown.module.scss';
import DropdownContainer from '../DropdownContainer/DropdownContainer';
import useClickOutside from '../../CustomHooks/useClickOutside';
import { useTranslation } from 'react-i18next';

type DropdownItem = {
  code: number | string;
  value: string;
};

interface DropdownProps {
  dropdownName: string;
  items: DropdownItem[] | undefined;
  selectedItem: DropdownItem | undefined;
  handleSelectItemCallback: (docType: DropdownItem) => void;
  addScroll?: boolean;
  isLoadingDropdown?: boolean;
  size?: 'sm' | 'md';
  className?: string;
  errorMsg?: string;
  invalid?: boolean;
}

const Dropdown: React.FC<DropdownProps> = ({
  dropdownName,
  items,
  selectedItem,
  handleSelectItemCallback,
  addScroll = false,
  isLoadingDropdown = false,
  size = 'md',
  className = '',
  errorMsg,
  invalid,
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const handleSelect = (item: DropdownItem, event: React.MouseEvent) => {
    event.stopPropagation();
    handleSelectItemCallback(item);
    setIsOpen(false);
  };

  const toggleDropdown = () => setIsOpen((prev) => !prev);

  useClickOutside(dropdownRef, () => setIsOpen(false));

  const renderLoader = () =>
    Array.from({ length: 5 }).map((_, index) => (
      <div key={index} className={`${styles.dropdown_item} ${styles.loader} animate`} />
    ));

  const renderItems = () =>
    items?.map((item, index) => (
      <div
        key={item?.code ?? index}
        className={`${styles.dropdown_item} ${selectedItem?.code === item?.code ? styles.selected : ''}`}
        onClick={(e) => handleSelect(item, e)}
      >
        {t(item?.value ?? '')}
      </div>
    ));

  return (
    <DropdownContainer
      label={dropdownName}
      value={t(selectedItem?.value ?? '')}
      size={size}
      className={className}
      errorMsg={errorMsg}
      invalid={invalid}
      addScroll={addScroll}
      onToggle={toggleDropdown}
      dropdownRef={dropdownRef}
      isOpen={isOpen}
    >
      <div className={isLoadingDropdown ? styles.dropdown_loader : ''}>
        {isLoadingDropdown ? renderLoader() : renderItems()}
      </div>
    </DropdownContainer>
  );
};

export default Dropdown;
